import _ from 'lodash'
import buildUrl from 'build-url'

import Utils from '@/utils/utils'
import SVGHelper from '@glass-designer/helpers/svgHelper'

import { InventoryStockTypes, UOMTypes, StockAssemblyStructureTypes } from '@/store/types'

export default {
  methods: {

    /**
     * Goes through all the nested order items in the order & generates the SVG display data diagrams for standard glass & assemblies
     * that are not generated by the order editor. Eg, just rectangles
     * Adds in the symbols for edgework too
     * Does not update order items generated by order editor
     * Applied to the flattened OrderItems edited by OrderItemList component
     * Mutates the OrderItemList in-place with the svgDisplayData field
     * 
     * Note: generateOrderItemStandardEdgeworkDetails() from OrderItemMixin MUST be run before this function to calculate edgework details per glass item
     * 
     * @param {Array<Object>} orderItemList - Array of objects containing the order item list
     * @param {Object} orderStampConfig - Object containing order stamp configuration details
     */
    generateOrderItemStandardDiagrams(orderItemList, orderStampConfig = {}) {
      let diagramItems = orderItemList.filter(orderItem => {
        if(orderItem.stockType != InventoryStockTypes.glass && orderItem.stockType != InventoryStockTypes.assembly)
          return false
        if(orderItem.isCancelled)
          return false
        return !orderItem.isEditorGeneratedItem
      })

      if(diagramItems.length == 0)
        return

      diagramItems.forEach(orderItem => {
        let childOrderItems = this._getNestedOrderItems(orderItem.id, orderItemList)

        if(orderItem.stockType == InventoryStockTypes.glass) {
          this._generateStandardGlassDiagrams(orderItem, childOrderItems, orderStampConfig)
        }

        if(orderItem.stockType == InventoryStockTypes.assembly) {
          this._generateStandardAssemblyDiagrams(orderItem, childOrderItems, orderStampConfig)
        }
      })
    },


    /**
     * Get the list of nested items for a parent
     * Includes assemblies, but does not go lower
     */
    _getNestedOrderItems(parentOrderItemId, orderItemList) {
      var outputList = []

      orderItemList.forEach(listItem => {
        if(listItem.parentOrderItemId == parentOrderItemId) {
          outputList.push(listItem)

          // Go through children, exception an assembly.
          // They get handled individually as there can be multiple levels for assemblies
          if(listItem.stockType != InventoryStockTypes.assembly) {
            outputList = _.concat(outputList, this._getNestedOrderItems(listItem.id, orderItemList))
          }
        }
      })

      return outputList
    },


    /**
     * Generate a diagram for a standard glass item (ie, not via order editor)
     */
    _generateStandardGlassDiagrams(glassOrderItem, childOrderItems, orderStampConfig) {
      let edgeworkDetailsList = glassOrderItem.edgeworkDetailsList || []

      // Calculate SVG
      let svgHelper = new SVGHelper()
      svgHelper.setPadding(35, 35, 35, 35)
      glassOrderItem.svgDisplayData = svgHelper.generateRectangleSVG({
        width: glassOrderItem.glassWidth,
        height: glassOrderItem.glassHeight,
        edgeworkDetailsList: edgeworkDetailsList, 
        childOrderItems: childOrderItems
      }, {
        showDimensions: true,
        showEdgeworkLegend: true,
        showStampLegend: true,
        showSquareCorners: true,
        showEdgeworkHighlights: true,
        showPanelItems: true,
        showAnchors: true,
        showBorder: true,
        svgHeight: '240px',
        scaleMaxWidth: 720,
        scaleMaxHeight: 240,
        orderStampConfig: orderStampConfig
      })

      // Label SVG with less info
      svgHelper.setPadding(1, 1, 1, 1)
      glassOrderItem.svgLabelData = svgHelper.generateRectangleSVG({
        width: glassOrderItem.glassWidth,
        height: glassOrderItem.glassHeight,
        edgeworkDetailsList: [],
        childOrderItems: childOrderItems
      }, {
        showDimensions: false,
        showEdgeworkLegend: false,
        showSquareCorners: true,
        centerDiagram: true,
        svgHeight: '240px',
        scaleMaxWidth: 240,
        scaleMaxHeight: 240
      })

      // Generate the label SVG corner data
      // Shows square corners & stamp position
      glassOrderItem.svgLabelCornerData = svgHelper.generateRectangleCornerDataSVG({
        width: glassOrderItem.glassWidth,
        height: glassOrderItem.glassHeight,
        childOrderItems: childOrderItems
      }, {
        svgHeight: '240px',
        scaleMaxWidth: 240,
        scaleMaxHeight: 240,
        orderStampConfig: orderStampConfig
      })
    },


    /**
     * Generate a diagram for a standard assembly item (ie, not via order editor)
     */
    _generateStandardAssemblyDiagrams(assemblyOrderItem, childOrderItems, orderStampConfig) {
      var isLayeredAssembly = assemblyOrderItem.stock.assemblyData.structureType == StockAssemblyStructureTypes.layered

      // Left side - Basic rectangle
      // Right side - Side view of layering

      // Calculate Layers
      var glassIndex = 0
      var patternLookup = {}
      var assemblyLayerList = []
      if(isLayeredAssembly) {
        assemblyLayerList = _.compact(childOrderItems.map(childOrderItem => {
          if(childOrderItem.stockType == InventoryStockTypes.glassService)
            return null

          // If general & not a production item, ignore it
          if(childOrderItem.stockType == InventoryStockTypes.general && !childOrderItem.stock.isProductionCompletable)
            return null

          let thickness = childOrderItem.stock.thickness || 0
          if(childOrderItem.stock.uomType == UOMTypes.length)
            thickness = childOrderItem.stock.dimWidth

          let name = `${childOrderItem.stock.name}`

          let colour = '#808080'
          
          // Patterns for metre^2 items
          if(childOrderItem.stock.uomType == UOMTypes.metresq) {

            // If already used a pattern for a stock, reuse it
            if(patternLookup[childOrderItem.stock.id]) {
              colour = patternLookup[childOrderItem.stock.id]
            } else {
              colour = `url(#pattern${glassIndex + 1})`
              glassIndex = (glassIndex + 1) % 2
              patternLookup[childOrderItem.stock.id] = colour
            }
          }

          return {
            id: childOrderItem.id,
            name: name,
            colour: colour,
            stockType: childOrderItem.stockType,
            uomType: childOrderItem.stock.uomType,
            profileType: childOrderItem.stock.profileType,
            glassWidth: childOrderItem.glassWidth,
            glassHeight: childOrderItem.glassHeight,
            stockFaceSideUpType: childOrderItem.stockFaceSideUpType || childOrderItem.defaultFaceSideUp,
            thickness: thickness,
            stock: _.pick(childOrderItem.stock, ['id', 'code', 'name', 'type', 'uomType', 'thickness', 'dimWidth', 'dimHeight', 'profileType', 'isStockUsingFaces', 'defaultFaceSideUp'])
          }
        }))
      }

      // Calculate SVG
      let svgHelper = new SVGHelper()
      svgHelper.setPadding(35, 35, 35, 35)
      assemblyOrderItem.svgDisplayData = svgHelper.generateRectangleAssemblySVG({
        width: assemblyOrderItem.glassWidth,
        height: assemblyOrderItem.glassHeight,
        childOrderItems: childOrderItems,
        layerList: assemblyLayerList
      }, {
        showDimensions: true,
        showSquareCorners: true,
        showEdgeworkHighlights: true,
        showAnchors: true,
        showSideView: isLayeredAssembly,
        showBorder: true,
        showLegend: true,
        svgHeight: '240px',
        scaleMaxWidth: 720,
        scaleMaxHeight: 240,
        orderStampConfig: orderStampConfig
      })

      // Label SVG with basic rectangle
      svgHelper.setPadding(1, 1, 1, 1)
      assemblyOrderItem.svgLabelData = svgHelper.generateRectangleSVG({
        width: assemblyOrderItem.glassWidth,
        height: assemblyOrderItem.glassHeight,
        edgeworkDetailsList: [],
        childOrderItems: childOrderItems
      }, {
        showDimensions: false,
        showEdgeworkLegend: false,
        showSquareCorners: true,
        centerDiagram: true,
        svgHeight: '240px',
        scaleMaxWidth: 240,
        scaleMaxHeight: 240
      })
    }
  }
}